import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import { AVAILABLE_BILL_MODALS } from '../../consts';
import { useStore } from '../../../../../../../../store';
import { cancelRequestVendorDetails } from './api/cancelRequestVendorDetails';
import { IBillItem } from '../../../../../../types';
import LiliToast, { LiliToastDefaultConfig } from '../../../../../../../../DesignSystem/LiliToast/LiliToast';

interface IProps {
    billId: string;
}

const CANCEL_BUTTON_STYLE = 'w-[120px] h-[40px] rounded leading-5 px-[14px] py-[10px] bg-red02 text-bw01 text-sm font-metricRSemibold';
const CANCEL_BUTTON_EXTRA_STYLE = 'hover:opacity-90 active:opacity-100';

const DONT_CANCEL_BUTTON_STYLE = 'w-[120px] h-[40px] rounded leading-5 px-[14px] py-[10px] bg-bw02 text-sm font-metricRSemibold text-action03';
const DONT_CANCEL_EXTRA_STYLE = 'hover:opacity-90 active:opacity-100';

export const CancelDetailsRequest = ({ billId } : IProps) => {
  const { t } = useTranslation();
  const setActiveBillModal = useStore(state => state.setActiveBillModal);
  const setSelectedBill = useStore(state => state.setSelectedBill);
  const setBillError = useStore(state => state.setBillError);
  const updateNewItemByExternalId = useStore(state => state.updateNewItemByExternalId);

  /** Queries */
  const { mutateAsync: cancelVendorDetails } = cancelRequestVendorDetails((billItem: IBillItem) => {
    setSelectedBill(billItem?.billId || '', billItem);
    updateNewItemByExternalId(billItem);
    setActiveBillModal(AVAILABLE_BILL_MODALS.NONE);
    toast(<LiliToast text={t('BILL_PAY.BILL_ACTIONS.CANCEL_SUCCESS')} id='cancelRequestSuccess' />, { ...LiliToastDefaultConfig });
  }, (errData) => {
    setBillError(errData);
    setActiveBillModal(AVAILABLE_BILL_MODALS.ERROR);
  });
    /** ------- */

  const handleSubmit = async () => {
    setActiveBillModal(AVAILABLE_BILL_MODALS.SPINNER);
    cancelVendorDetails({ billId });
  };

  const handleCancel = () => {
    setActiveBillModal(AVAILABLE_BILL_MODALS.NONE);
  };

  return (
    <div
      data-testid='BillsHub__Modal__Mark-as-Unpaid'
      className='flex flex-nowrap flex-col w-[343px] bg-bw01 rounded-lg border-[1px] border-bw03 px-[24px] pt-[34px] pb-[24px] box-border animate-fadeIn'
    >
      <div className='font-metricRMedium text-lg text-bw08 leading-6 mb-[8px] text-center'>
        {t('BILL_PAY.CANCEL_DETAILS_REQUEST.TITLE')}
      </div>
      <div className='font-metricR text-md text-bw07 leading-6 mb-[22px] text-center'>
        {t('BILL_PAY.CANCEL_DETAILS_REQUEST.SUBTITLE')}
      </div>
      <div className='flex justify-center gap-4'>
        <LiliButton
          id='billsHub-cancelDetailsRequest-buttonCancel'
          onClick={handleCancel}
          type={LiliButtonType.TERTIARY}
          title={t('BILL_PAY.CANCEL_DETAILS_REQUEST.BUTTON_CANCEL')}
          className={`${DONT_CANCEL_BUTTON_STYLE} ${DONT_CANCEL_EXTRA_STYLE}`}
        />
        <LiliButton
          id='billsHub-cancelDetailsRequest-buttonUnpaid'
          onClick={handleSubmit}
          type={LiliButtonType.DESTRUCTIVE}
          title={t('BILL_PAY.CANCEL_DETAILS_REQUEST.BUTTON_SUBMIT')}
          className={`${CANCEL_BUTTON_STYLE} ${CANCEL_BUTTON_EXTRA_STYLE}`}
        />
      </div>
    </div>
  );
};
